<script setup>
import { computed, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useStore } from 'vuex';

import Input from 'dashboard/components-next/input/Input.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import {
  findComponentByType,
  COMPONENT_TYPES,
} from 'dashboard/helper/templateHelper';
import { useCampaignWizard } from '../composables/useCampaignWizard';
// GK-HUB: Team + assignee selectors for campaign conversations
import { useMapGetter } from 'dashboard/composables/store';

const { t } = useI18n();
const store = useStore();
const { wizardState, firstContact } = useCampaignWizard();

// GK-HUB: Team selector
const teams = useMapGetter('teams/getTeams');
const selectedTeamName = computed(() => {
  if (!wizardState.teamId) return t('CAMPAIGN.WHATSAPP.WIZARD.NO_TEAM');
  const team = (teams.value || []).find(tm => tm.id === wizardState.teamId);
  return team ? team.name : '-';
});
// GK-HUB: assignee selector — filtered by selected team
const teamMembers = computed(() => {
  if (!wizardState.teamId) return [];
  return store.getters['teamMembers/getTeamMembers'](wizardState.teamId) || [];
});

// Only show role:agent members (admin/manager/supervisor excluded from auto-assignment).
// Backend (Campaign model + TeamRoutingService) revalida o role — esse filtro é UX, não segurança.
const teamAgents = computed(() => {
  return teamMembers.value.filter(m => m.role === 'agent');
});

// Fetch team members when team changes
watch(
  () => wizardState.teamId,
  async (teamId, oldTeamId) => {
    if (teamId !== oldTeamId) {
      wizardState.assigneeId = null; // reset assignee on team change
    }
    if (teamId) {
      await store.dispatch('teamMembers/get', { teamId });
    }
  },
  { immediate: true }
);
// GK-HUB: end assignee selector

// GK-HUB: end team selector

const currentDateTime = computed(() => {
  const now = new Date();
  const localTime = new Date(now.getTime() - now.getTimezoneOffset() * 60000);
  return localTime.toISOString().slice(0, 16);
});

const audienceLabel = computed(() => {
  if (wizardState.audienceMode === 'spreadsheet') {
    return `${t('CAMPAIGN.WHATSAPP.WIZARD.MODE_SPREADSHEET')} — ${wizardState.uploadedContacts.length} ${t('CAMPAIGN.WHATSAPP.WIZARD.CONTACTS_LABEL')}`;
  }
  // GK-HUB: ERP segment audience label
  if (wizardState.audienceMode === 'slash_command') {
    const seg = wizardState.n8nSegments.find(
      s => s.id === wizardState.selectedSegmentId
    );
    const segLabel = seg ? `/${seg.id}` : '';
    return `${t('CAMPAIGN.WHATSAPP.WIZARD.MODE_ERP_SEGMENT')} ${segLabel} — ${wizardState.segmentContacts.length} ${t('CAMPAIGN.WHATSAPP.WIZARD.CONTACTS_LABEL')}`;
  }
  return `${t('CAMPAIGN.WHATSAPP.WIZARD.MODE_LABELS')} — ${wizardState.selectedLabels.length} labels`;
});

const templateName = computed(() => {
  return wizardState.selectedTemplate?.name || '-';
});

const mappingEntries = computed(() => {
  return Object.entries(wizardState.variableMapping).map(([key, config]) => {
    const label =
      config.type === 'field'
        ? `${config.field} (${t('CAMPAIGN.WHATSAPP.WIZARD.REVIEW_FIELD')})`
        : `${config.value} (${t('CAMPAIGN.WHATSAPP.WIZARD.REVIEW_FIXED_VALUE')})`;
    return { key, label };
  });
});

const ignoredCount = computed(() => {
  const w = wizardState.uploadWarnings;
  if (!w) return 0;
  return (w.missing_phone || 0) + (w.invalid_phone || 0) + (w.duplicates || 0);
});

const totalToSend = computed(() => {
  if (wizardState.audienceMode === 'spreadsheet') {
    return wizardState.uploadedContacts.length;
  }
  // GK-HUB: N8N segment total
  if (wizardState.audienceMode === 'slash_command') {
    return wizardState.segmentContacts.length;
  }
  return null;
});

// Preview text using first contact
const previewText = computed(() => {
  if (!wizardState.selectedTemplate) return '';
  const body = findComponentByType(
    wizardState.selectedTemplate,
    COMPONENT_TYPES.BODY
  );
  if (!body?.text) return '';

  let text = body.text;
  const mapping = wizardState.variableMapping;
  const contact = firstContact.value || {};

  Object.entries(mapping).forEach(([varKey, config]) => {
    let resolvedValue = '';
    if (config.type === 'field' && config.field) {
      resolvedValue = contact[config.field] || `[${config.field}]`;
    } else if (config.type === 'fixed' && config.value) {
      resolvedValue = config.value;
    } else {
      resolvedValue = `{{${varKey}}}`;
    }
    text = text.replace(`{{${varKey}}}`, resolvedValue);
  });

  return text;
});

const formatVarKey = key => '{{' + key + '}}';

const setScheduleNow = () => {
  wizardState.scheduledAt = currentDateTime.value;
};

// GK-HUB: R6 — Throttle + retry advanced settings
const showAdvanced = ref(false);

const throttleOptions = [
  { value: 0, label: t('CAMPAIGN.WHATSAPP.WIZARD.THROTTLE_NONE') },
  { value: 100, label: t('CAMPAIGN.WHATSAPP.WIZARD.THROTTLE_100MS') },
  { value: 500, label: t('CAMPAIGN.WHATSAPP.WIZARD.THROTTLE_500MS') },
  { value: 1000, label: t('CAMPAIGN.WHATSAPP.WIZARD.THROTTLE_1S') },
  { value: 2000, label: t('CAMPAIGN.WHATSAPP.WIZARD.THROTTLE_2S') },
];

const retryOptions = [
  { value: 0, label: t('CAMPAIGN.WHATSAPP.WIZARD.RETRY_DISABLED') },
  { value: 1, label: t('CAMPAIGN.WHATSAPP.WIZARD.RETRY_1') },
  { value: 2, label: t('CAMPAIGN.WHATSAPP.WIZARD.RETRY_2') },
  { value: 3, label: t('CAMPAIGN.WHATSAPP.WIZARD.RETRY_3') },
];

const throttleSummary = computed(() => {
  if (!wizardState.throttleIntervalMs) return null;
  const opt = throttleOptions.find(
    o => o.value === wizardState.throttleIntervalMs
  );
  return opt ? opt.label : `${wizardState.throttleIntervalMs}ms`;
});

const retrySummary = computed(() => {
  if (!wizardState.maxRetries) return null;
  const opt = retryOptions.find(o => o.value === wizardState.maxRetries);
  return opt ? opt.label : `${wizardState.maxRetries}x`;
});
// GK-HUB: end R6 advanced settings
</script>

<template>
  <div class="flex flex-col gap-6">
    <h3 class="text-base font-medium text-n-slate-12">
      {{ t('CAMPAIGN.WHATSAPP.WIZARD.REVIEW_TITLE') }}
    </h3>

    <!-- Campaign Name -->
    <Input
      v-model="wizardState.title"
      :label="t('CAMPAIGN.WHATSAPP.WIZARD.CAMPAIGN_NAME')"
      :placeholder="t('CAMPAIGN.WHATSAPP.CREATE.FORM.TITLE.PLACEHOLDER')"
    />

    <!-- GK-HUB: Team assignment for campaign conversations -->
    <div class="flex flex-col gap-2">
      <label class="text-sm font-medium text-n-slate-12">
        {{ t('CAMPAIGN.WHATSAPP.WIZARD.TEAM_LABEL') }}
      </label>
      <p class="text-xs text-n-slate-10">
        {{ t('CAMPAIGN.WHATSAPP.WIZARD.TEAM_HINT') }}
      </p>
      <select
        v-model="wizardState.teamId"
        class="w-full rounded-lg border border-n-weak bg-n-alpha-black2 px-3 py-2 text-sm text-n-slate-12 focus:border-n-brand focus:outline-none focus:ring-1 focus:ring-n-brand"
      >
        <option :value="null">
          {{ t('CAMPAIGN.WHATSAPP.WIZARD.NO_TEAM') }}
        </option>
        <option v-for="team in teams" :key="team.id" :value="team.id">
          {{ team.name }}
        </option>
      </select>
    </div>
    <!-- GK-HUB: end team assignment -->

    <!-- GK-HUB: Assignee selector (depends on selected team) -->
    <div class="flex flex-col gap-2">
      <label class="text-sm font-medium text-n-slate-12">
        {{ t('CAMPAIGN.WHATSAPP.WIZARD.ASSIGNEE_LABEL') }}
      </label>
      <p class="text-xs text-n-slate-10">
        {{
          wizardState.teamId
            ? t('CAMPAIGN.WHATSAPP.WIZARD.ASSIGNEE_HINT')
            : t('CAMPAIGN.WHATSAPP.WIZARD.ASSIGNEE_REQUIRES_TEAM')
        }}
      </p>
      <select
        v-model="wizardState.assigneeId"
        :disabled="!wizardState.teamId || teamAgents.length === 0"
        class="w-full rounded-lg border border-n-weak bg-n-alpha-black2 px-3 py-2 text-sm text-n-slate-12 focus:border-n-brand focus:outline-none focus:ring-1 focus:ring-n-brand disabled:opacity-50 disabled:cursor-not-allowed"
      >
        <option :value="null">
          {{ t('CAMPAIGN.WHATSAPP.WIZARD.NO_ASSIGNEE') }}
        </option>
        <option v-for="agent in teamAgents" :key="agent.id" :value="agent.id">
          {{ agent.name }}
        </option>
      </select>
      <p
        v-if="wizardState.teamId && teamAgents.length === 0"
        class="text-xs text-n-amber-11"
      >
        {{ t('CAMPAIGN.WHATSAPP.WIZARD.ASSIGNEE_EMPTY') }}
      </p>
    </div>
    <!-- GK-HUB: end assignee selector -->

    <!-- Summary Card -->
    <div class="flex flex-col gap-3 p-4 rounded-xl bg-n-alpha-black2">
      <div class="flex justify-between items-center">
        <span class="text-sm text-n-slate-11">
          {{ t('CAMPAIGN.WHATSAPP.WIZARD.REVIEW_AUDIENCE') }}
        </span>
        <span class="text-sm font-medium text-n-slate-12">
          {{ audienceLabel }}
        </span>
      </div>

      <div class="flex justify-between items-center">
        <span class="text-sm text-n-slate-11">
          {{ t('CAMPAIGN.WHATSAPP.WIZARD.REVIEW_TEMPLATE') }}
        </span>
        <span class="text-sm font-medium text-n-slate-12">
          {{ templateName }}
        </span>
      </div>

      <div
        v-if="mappingEntries.length > 0"
        class="flex flex-col gap-1 pt-2 border-t border-n-weak"
      >
        <span class="text-sm text-n-slate-11 mb-1">
          {{ t('CAMPAIGN.WHATSAPP.WIZARD.REVIEW_MAPPING') }}
        </span>
        <div
          v-for="entry in mappingEntries"
          :key="entry.key"
          class="flex justify-between items-center text-sm"
        >
          <span class="font-mono text-n-teal-11">{{
            formatVarKey(entry.key)
          }}</span>
          <span class="text-n-slate-12">{{ entry.label }}</span>
        </div>
      </div>

      <div
        v-if="ignoredCount > 0"
        class="flex justify-between items-center pt-2 border-t border-n-weak"
      >
        <span class="text-sm text-n-slate-11">
          {{ t('CAMPAIGN.WHATSAPP.WIZARD.REVIEW_IGNORED') }}
        </span>
        <span class="text-sm text-n-amber-11">
          {{ ignoredCount }}
          {{ t('CAMPAIGN.WHATSAPP.WIZARD.CONTACTS_LABEL') }}
        </span>
      </div>

      <div
        v-if="totalToSend"
        class="flex justify-between items-center pt-2 border-t border-n-weak"
      >
        <span class="text-sm text-n-slate-11">
          {{ t('CAMPAIGN.WHATSAPP.WIZARD.REVIEW_TOTAL') }}
        </span>
        <span class="text-sm font-medium text-n-teal-11">
          {{
            t('CAMPAIGN.WHATSAPP.WIZARD.MESSAGES_WILL_SEND', {
              count: totalToSend,
            })
          }}
        </span>
      </div>

      <!-- GK-HUB: Team assignment summary -->
      <div
        class="flex justify-between items-center pt-2 border-t border-n-weak"
      >
        <span class="text-sm text-n-slate-11">
          {{ t('CAMPAIGN.WHATSAPP.WIZARD.REVIEW_TEAM') }}
        </span>
        <span class="text-sm font-medium text-n-slate-12">
          {{ selectedTeamName }}
        </span>
      </div>
      <!-- GK-HUB: end team summary -->
    </div>

    <!-- Preview -->
    <div
      v-if="previewText"
      class="flex flex-col gap-2 p-4 rounded-xl bg-n-alpha-black2"
    >
      <h4 class="text-sm font-medium text-n-slate-12">
        {{ t('CAMPAIGN.WHATSAPP.WIZARD.PREVIEW_TITLE') }}
      </h4>
      <div
        class="p-3 rounded-lg bg-n-teal-9/10 border border-n-teal-9/20 text-sm text-n-slate-12 whitespace-pre-wrap"
      >
        {{ previewText }}
      </div>
    </div>

    <!-- Schedule -->
    <div class="flex flex-col gap-2">
      <label class="text-sm font-medium text-n-slate-12">
        {{ t('CAMPAIGN.WHATSAPP.WIZARD.SCHEDULE_LABEL') }}
      </label>
      <div class="flex items-center gap-3">
        <Input
          v-model="wizardState.scheduledAt"
          type="datetime-local"
          :min="currentDateTime"
          class="flex-1"
        />
        <Button
          variant="faded"
          color="slate"
          :label="t('CAMPAIGN.WHATSAPP.WIZARD.SEND_NOW')"
          size="sm"
          class="whitespace-nowrap"
          @click="setScheduleNow"
        />
      </div>
    </div>

    <!-- GK-HUB: R6 — Advanced settings (throttle + retry) -->
    <div class="flex flex-col gap-3">
      <button
        type="button"
        class="flex items-center gap-2 text-sm text-n-slate-11 hover:text-n-slate-12 transition-colors"
        @click="showAdvanced = !showAdvanced"
      >
        <!-- eslint-disable vue/no-bare-strings-in-template -->
        <span
          class="transition-transform"
          :class="showAdvanced ? 'rotate-90' : ''"
        >
          &#9654;
        </span>
        <!-- eslint-enable vue/no-bare-strings-in-template -->
        {{ t('CAMPAIGN.WHATSAPP.WIZARD.ADVANCED_SETTINGS') }}
      </button>

      <div
        v-if="showAdvanced"
        class="flex flex-col gap-4 p-4 rounded-xl bg-n-alpha-black2"
      >
        <!-- Throttle -->
        <div class="flex flex-col gap-1">
          <label
            for="throttle-select"
            class="text-sm font-medium text-n-slate-12"
          >
            {{ t('CAMPAIGN.WHATSAPP.WIZARD.THROTTLE_LABEL') }}
          </label>
          <select
            id="throttle-select"
            v-model.number="wizardState.throttleIntervalMs"
            class="w-full rounded-lg border border-n-weak bg-n-alpha-black2 px-3 py-2 text-sm text-n-slate-12 focus:border-n-brand focus:outline-none focus:ring-1 focus:ring-n-brand"
          >
            <option
              v-for="opt in throttleOptions"
              :key="opt.value"
              :value="opt.value"
            >
              {{ opt.label }}
            </option>
          </select>
        </div>

        <!-- Retry -->
        <div class="flex flex-col gap-1">
          <label for="retry-select" class="text-sm font-medium text-n-slate-12">
            {{ t('CAMPAIGN.WHATSAPP.WIZARD.RETRY_LABEL') }}
          </label>
          <select
            id="retry-select"
            v-model.number="wizardState.maxRetries"
            class="w-full rounded-lg border border-n-weak bg-n-alpha-black2 px-3 py-2 text-sm text-n-slate-12 focus:border-n-brand focus:outline-none focus:ring-1 focus:ring-n-brand"
          >
            <option
              v-for="opt in retryOptions"
              :key="opt.value"
              :value="opt.value"
            >
              {{ opt.label }}
            </option>
          </select>
          <p class="text-xs text-n-slate-10">
            {{ t('CAMPAIGN.WHATSAPP.WIZARD.RETRY_HINT') }}
          </p>
        </div>
      </div>

      <!-- Summary in review card -->
      <div
        v-if="throttleSummary || retrySummary"
        class="flex flex-col gap-1 text-sm"
      >
        <div v-if="throttleSummary" class="flex justify-between items-center">
          <span class="text-n-slate-11">
            {{ t('CAMPAIGN.WHATSAPP.WIZARD.THROTTLE_LABEL') }}
          </span>
          <span class="font-medium text-n-slate-12">
            {{ throttleSummary }}
          </span>
        </div>
        <div v-if="retrySummary" class="flex justify-between items-center">
          <span class="text-n-slate-11">
            {{ t('CAMPAIGN.WHATSAPP.WIZARD.RETRY_LABEL') }}
          </span>
          <span class="font-medium text-n-slate-12">
            {{ retrySummary }}
          </span>
        </div>
      </div>
    </div>
    <!-- GK-HUB: end R6 advanced settings -->
  </div>
</template>
